import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { PersonalSheet } from "@/features/self/profile/types/personalSheet";
import type { DraftSectionKey, DraftStepKey, PersonalSheetSections } from "../types";
import {
  useGetPersonalSheetDraftQuery,
  useAutosavePersonalSheetDraftMutation,
  useCompletePersonalSheetMutation,
} from "../api";

export const DRAFT_SECTIONS: DraftStepKey[] = [
  "general",
  "contacts",
  "addresses",
  "emergency_contacts",
  "id_documents",
  "banking",
  "education",
  "experience",
  "social_categories",
  "languages",
  "scientific_works",
  "government_awards",
  "kyc_documents",
];

function toSectionKey(step: DraftStepKey): DraftSectionKey {
  if (step === "kyc_documents") return "general";
  return step;
}

function normalizeValue(value: unknown): unknown {
  if (value === null || value === undefined || value === "") return null;
  if (Array.isArray(value)) return value.map(normalizeValue);
  if (value instanceof Date) return value.toISOString();
  if (typeof value !== "object") return value;
  return Object.keys(value as Record<string, unknown>)
    .sort()
    .reduce<Record<string, unknown>>((acc, key) => {
      acc[key] = normalizeValue((value as Record<string, unknown>)[key]);
      return acc;
    }, {});
}

function dataSignature(data: PersonalSheet): string {
  return JSON.stringify(normalizeValue(data));
}

function sectionSignature(value: unknown): string {
  return JSON.stringify(normalizeValue(value));
}

function recordSignature(value: unknown): string {
  if (value === null || value === undefined || value === "") return "null";
  if (Array.isArray(value)) return JSON.stringify(value.map(recordSignature));
  if (value instanceof Date) return value.toISOString();
  if (typeof value !== "object") return JSON.stringify(value);
  const normalized = Object.keys(value as Record<string, unknown>)
    .filter((key) => key !== "id")
    .sort()
    .reduce<Record<string, unknown>>((acc, key) => {
      acc[key] = normalizeValue((value as Record<string, unknown>)[key]);
      return acc;
    }, {});
  return JSON.stringify(normalized);
}

function dedupeRecords<T>(items: T[]): T[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    const signature = recordSignature(item);
    if (seen.has(signature)) return false;
    seen.add(signature);
    return true;
  });
}

function dedupeDraftData(data: PersonalSheet): PersonalSheet {
  return {
    ...data,
    emergency_contacts: dedupeRecords(data.emergency_contacts),
    education: dedupeRecords(data.education),
    experience: dedupeRecords(data.experience),
    social_categories: dedupeRecords(data.social_categories),
    languages: dedupeRecords(data.languages),
    scientific_works: dedupeRecords(data.scientific_works),
    government_awards: dedupeRecords(data.government_awards),
  };
}

export function buildSections(data: PersonalSheet): PersonalSheetSections {
  return {
    general: data.general
      ? {
          surname: data.general.surname,
          name: data.general.name,
          father_name: data.general.father_name,
          gender: data.general.gender,
          family_status: data.general.family_status,
          nationality: data.general.nationality,
          citizenship: data.general.citizenship,
          is_resident: data.general.is_resident,
          is_student: data.general.is_student,
          has_no_education: data.general.has_no_education,
          has_no_work_experience: data.general.has_no_work_experience,
          has_no_scientific_works: data.general.has_no_scientific_works,
          has_no_government_awards: data.general.has_no_government_awards,
          has_no_social_categories: data.general.has_no_social_categories,
          enrollment_start_date: data.general.enrollment_start_date,
          enrollment_end_date: data.general.enrollment_end_date,
          city_of_birth_id: data.general.city_of_birth?.id,
          city_of_birth_other: data.general.city_of_birth_other,
        }
      : undefined,
    contacts: data.contacts
      ? {
          email: data.contacts.email,
          phone: data.contacts.phone,
          phone_additional: data.contacts.phone_additional,
        }
      : undefined,
    addresses: data.addresses
      ? {
          address_registration: data.addresses.address_registration,
          address_factual: data.addresses.address_factual,
        }
      : undefined,
    id_documents: data.id_documents
      ? {
          national_id_number: data.id_documents.national_id_number,
          national_id_issued_by: data.id_documents.national_id_issued_by,
          national_id_issue_date: data.id_documents.national_id_issue_date ?? undefined,
          national_id_expiry_date: data.id_documents.national_id_expiry_date ?? undefined,
          military_obligation_relation: data.id_documents.military_obligation_relation,
          military_rank: data.id_documents.military_rank,
          military_composition: data.id_documents.military_composition,
          military_branch: data.id_documents.military_branch,
        }
      : undefined,
    banking: data.banking
      ? {
          bank_name: data.banking.bank_name,
          iban_account: data.banking.iban_account,
          bik_number: data.banking.bik_number,
        }
      : undefined,
    emergency_contacts: data.emergency_contacts.map((c) => ({
      id: c.id,
      contact_name: c.contact_name,
      contact_phone: c.contact_phone,
      contact_relation: c.contact_relation,
      birth_year: c.birth_year,
    })),
    education: data.education.map((e) => ({
      id: e.id,
      degree: e.degree,
      university_name: e.university_name,
      specialty: e.specialty,
      admission_year: e.admission_year,
      is_incomplete: e.is_incomplete ?? false,
      graduation_year: e.graduation_year,
      diploma_number: e.diploma_number,
      left_from_course: e.left_from_course ?? "",
      ...(e.diploma_file_token ? { diploma_file_token: e.diploma_file_token } : {}),
      ...(e.diploma_file === null ? { diploma_file: null } : {}),
      ...(e.diploma_transcript_file_token ? { diploma_transcript_file_token: e.diploma_transcript_file_token } : {}),
      ...(e.diploma_transcript_file === null ? { diploma_transcript_file: null } : {}),
    })),
    experience: data.experience.map((e) => ({
      id: e.id,
      organization_name: e.organization_name,
      speciality: e.speciality,
      work_position: e.work_position,
      work_format: e.work_format,
      work_address: e.work_address ?? "",
      beginning_date: e.beginning_date,
      end_date: e.end_date,
    })),
    languages: data.languages.map((l) => ({
      id: l.id,
      language: l.language,
      proficiency_level: l.proficiency_level,
    })),
    scientific_works: data.scientific_works.map((w) => ({
      id: w.id,
      title: w.title,
      year: w.year,
      ...(w.file_token ? { file_token: w.file_token } : {}),
      ...(w.file === null ? { file: null } : {}),
    })),
    government_awards: data.government_awards.map((a) => ({
      id: a.id,
      award_name: a.award_name,
      issued_by: a.issued_by,
      issued_date: a.issued_date,
    })),
    social_categories: data.social_categories.map((c) => ({
      id: c.id,
      category_type: c.category_type,
      issue_date: c.issue_date,
      expiry_date: c.expiry_date,
      notes: c.notes,
      ...(c.document_file_token ? { document_file_token: c.document_file_token } : {}),
      ...(c.document_file === null ? { document_file: null } : {}),
    })),
  };
}

export function usePersonalSheetDraft() {
  const { t } = useTranslation("PersonalSheetDraftPage");
  const { data: draftResponse, isLoading: isDraftLoading, error: draftError } = useGetPersonalSheetDraftQuery();
  const [autosave] = useAutosavePersonalSheetDraftMutation();
  const [completeDraft, { isLoading: isCompleting }] = useCompletePersonalSheetMutation();

  const [draftData, setDraftData] = useState<PersonalSheet | null>(null);
  const [activeSection, setActiveSection] = useState<DraftStepKey>("general");
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);

  const activeSectionRef = useRef<DraftSectionKey>("general");
  const draftDataRef = useRef<PersonalSheet | null>(null);
  const lastSavedSignatureRef = useRef<string | null>(null);

  useEffect(() => {
    if (draftResponse && !draftData) {
      const dedupedData = dedupeDraftData(draftResponse.data);
      setDraftData(dedupedData);
      draftDataRef.current = dedupedData;
      lastSavedSignatureRef.current = dataSignature(dedupedData);
      setActiveSection(draftResponse.last_active_section);
      activeSectionRef.current = draftResponse.last_active_section;
    }
  }, [draftResponse, draftData]);

  const handleSectionChange = useCallback(
    function handleSectionChange(key: DraftSectionKey, value: PersonalSheet[keyof PersonalSheet]) {
      const current = draftDataRef.current;
      if (!current) return;
      const next = dedupeDraftData({ ...current, [key]: value });
      if (sectionSignature(current[key]) === sectionSignature(next[key])) return;
      draftDataRef.current = next;
      setDraftData(next);
    },
    [],
  );

  function handleActiveSectionChange(step: DraftStepKey) {
    setActiveSection(step);
    const sectionKey = toSectionKey(step);
    activeSectionRef.current = sectionKey;
    const latest = draftDataRef.current;
    if (!latest) return;
    const currentSig = dataSignature(latest);
    if (currentSig === lastSavedSignatureRef.current) return;
    lastSavedSignatureRef.current = currentSig;
    autosave({ data: latest, last_active_section: sectionKey });
  }

  function handleNext() {
    const currentIndex = DRAFT_SECTIONS.indexOf(activeSection);
    if (currentIndex < DRAFT_SECTIONS.length - 1) {
      handleActiveSectionChange(DRAFT_SECTIONS[currentIndex + 1]);
    }
  }

  function buildCompleteDto() {
    if (!draftData) return null;
    return { sections: buildSections(draftData) };
  }

  useEffect(() => {
    function flushAutosave() {
      const latest = draftDataRef.current;
      if (!latest) return;
      const currentSig = dataSignature(latest);
      if (currentSig === lastSavedSignatureRef.current) return;
      lastSavedSignatureRef.current = currentSig;
      autosave({ data: latest, last_active_section: activeSectionRef.current });
    }
    window.addEventListener("beforeunload", flushAutosave);
    return () => {
      window.removeEventListener("beforeunload", flushAutosave);
    };
  }, [autosave]);

  return {
    t,
    draftData,
    isDraftLoading,
    draftError,
    activeSection,
    handleActiveSectionChange,
    showCompleteConfirm,
    setShowCompleteConfirm,
    completeDraft,
    isCompleting,
    handleNext,
    handleSectionChange,
    buildCompleteDto,
  };
}
